// "This pack has no rate card" → the person who can add one. An admin gets a
// link that opens Financial → Rate Card → New rate card filled in with the
// pack code and fruit (?new=&fruit=, read once by RateCardTab); Back returns
// here. Anyone else gets the admins' names and a link to copy for one of them,
// because rate cards are admin-only at the database (F19 D2). Used by the line
// preset form ("Cost this pack", F19 UF-14) and Commodities → Pack formats
// ("Price this pack", F20 UF-01).
import { useState } from 'react'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { canWriteRateCards, tenantRoleOf } from '@/lib/roles'
import { usePlatformView } from '@/lib/platformView'
import { useNavigateWithReturn } from '@/lib/returnTo'
import { useBusinessAdmins, adminsPhrase } from '@/hooks/useBusinessAdmins'
import { confirmLeavePage } from '@/lib/pageDirty'
import { Button } from '@/components/ui'

export function newRateCardPath(code: string, fruit: string | null | undefined): string {
  const q = new URLSearchParams({ tab: 'rate_card', new: code })
  if (fruit) q.set('fruit', fruit)
  return `/financial?${q.toString()}`
}

/** Open an EXISTING card: RateCardTab reads ?product=<id>. */
export function openRateCardPath(productId: string): string {
  return `/financial?${new URLSearchParams({ tab: 'rate_card', product: productId }).toString()}`
}

/** UF-06: the sentence a non-admin copies for an admin; the link follows it. */
export function rateCardRequestMessage(code: string, fruit: string | null | undefined, runNumber?: string | null): string {
  return `Please set up a rate card for ${code}${fruit ? ` (${fruit})` : ''}${runNumber ? ` for run ${runNumber}` : ''}`
}

type HandoffProps = {
  code: string; fruit?: string | null; label: string
  /** Non-admin seats: copy this request (then the link) instead of the bare
   *  link, and word the button "Copy request for an admin". */
  message?: string
  /** Open this existing card instead of starting a new one. */
  productId?: string
  /** Open in a new tab. For a screen holding unsaved component state that no
   *  page dirty guard covers (New Pallet): leaving it would lose the form. */
  newTab?: boolean
  /** Where Back from the rate card lands, when the current URL does not name
   *  the view (Commodities: the fruit and its Pack formats list). Omitted =
   *  the current location, as before. Must be an in-app absolute path. */
  returnPath?: string
}

function AdminHandoff({ path, label, newTab, returnPath }: { path: string; label: string; newTab?: boolean; returnPath?: string }) {
  const go = useNavigateWithReturn(returnPath)
  if (newTab) {
    return (
      <a href={path} target="_blank" rel="noopener noreferrer"
        className="btn-secondary btn-sm tap-target-y">
        {label}
      </a>
    )
  }
  return (
    <Button variant="secondary" size="sm"
      // confirmLeavePage() only holds the user on pages that arm
      // usePageDirtyGuard. A form kept in component state without the guard
      // must pass newTab instead, so the form survives.
      onClick={() => { void confirmLeavePage().then(ok => { if (ok) go(path) }) }}>
      {label}
    </Button>
  )
}

function CopyHandoff({ path, message }: { path: string; message?: string }) {
  const admins = useBusinessAdmins()
  const [copied, setCopied] = useState<'yes' | 'no' | null>(null)
  const url = `${window.location.origin}${path}`
  const copyText = message ? `${message}: ${url}` : url
  // Names only when the seat can read members (useBusinessAdmins returns null otherwise).
  const copyLabel = message
    ? `Copy request for ${admins && admins.length === 1 ? admins[0] : 'an admin'}`
    : 'Copy link for an admin'
  return (
    <span className="text-caption text-fg-secondary inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      {!message && <span>Rate cards are added by {adminsPhrase(admins)}.</span>}
      <Button variant="secondary" size="sm"
        onClick={() => { navigator.clipboard.writeText(copyText).then(() => setCopied('yes'), () => setCopied('no')) }}>
        {copyLabel}
      </Button>
      {copied === 'yes' && <span className="text-success">Copied.</span>}
      {copied === 'no' && <span className="select-all font-mono text-fg-secondary">{copyText}</span>}
    </span>
  )
}

export function RateCardHandoff({ code, fruit, label, productId, newTab, message, returnPath }: HandoffProps) {
  const { data: me } = useCurrentUser()
  const canWrite = canWriteRateCards(tenantRoleOf(me), usePlatformView())
  const path = productId ? openRateCardPath(productId) : newRateCardPath(code, fruit)
  return canWrite
    ? <AdminHandoff path={path} label={label} newTab={newTab} returnPath={returnPath} />
    : <CopyHandoff path={path} message={message} />
}

/** UF-06: one hand-off per code struck for a MISSING card. `unreadable` is
 *  RB1 D1's null ("this seat cannot read cards"): it cannot tell, so it asks
 *  for nothing rather than for a card that may exist. */
export function MissingCardRequests({ codes, fruit, fruitName, runNumber, unreadable }: {
  codes: readonly string[]; fruit: string | null | undefined; fruitName?: string | null
  runNumber?: string | null; unreadable?: boolean
}) {
  if (unreadable) return null
  return (
    <>
      {codes.map(c => (
        <div key={c}>
          <RateCardHandoff newTab code={c} fruit={fruit} label={`Add a rate card for ${c}`}
            message={rateCardRequestMessage(c, fruitName ?? fruit, runNumber)} />
        </div>
      ))}
    </>
  )
}
